/** @jsx jsx */
import React from 'react';
import { css, styled, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import type { JiraNotificationType } from '@atlassian/jira-relay/src/__generated__/notificationsAccordianRow_personalSettingsNotificationsConfigTable_NotificationsConfigRow_allPreferences.graphql';
import type { JiraNotificationChannelType } from '@atlassian/jira-relay/src/__generated__/notificationsConfigRow_personalSettingsNotificationsConfigTable_allPreferences.graphql';
import type { HeadCheckboxIdsType, JiraNotificationPreference } from '../../types.tsx';
import { NotificationsConfigCheckbox } from '../notifications-config-checkbox/index.tsx';

export const NotificationsConfigRow = ({
	headCheckboxIds,
	preference,
	label,
	isOpen,
	onChange,
	isLastChild,
	id,
	isEmailNotificationDisabled = false,
}: {
	headCheckboxIds: HeadCheckboxIdsType;
	preference: JiraNotificationPreference;
	label: string;
	isOpen: boolean;
	isLastChild: boolean;
	onChange: (
		e: React.ChangeEvent<HTMLInputElement>,
		channel: JiraNotificationChannelType,
		type: JiraNotificationType | null | undefined,
	) => void;
	id: string;
	isEmailNotificationDisabled?: boolean;
}) => {
	const { type } = preference;
	const emailCheckboxId = `email-${id}`;
	const inProductCheckboxId = `inProduct-${id}`;

	return (
		<TableRowContainer css={[!isOpen && hidden, isLastChild && border]}>
			<CellContainer id={id}>{label}</CellContainer>
			<CellContainer>
				{preference.inProductChannel?.isEditable && (
					<NotificationsConfigCheckbox
						aria-labelledby={`${id} ${headCheckboxIds.IN_PRODUCT}`}
						id={inProductCheckboxId}
						name={inProductCheckboxId}
						testId={`personal-settings-notifications-config-table.ui.notifications-config-row-lite.${inProductCheckboxId}`}
						isChecked={!!preference.inProductChannel.isEnabled}
						onChange={(ev) => onChange(ev, 'IN_PRODUCT', type)}
					/>
				)}
			</CellContainer>
			<CellContainer>
				{preference.emailChannel?.isEditable && (
					<NotificationsConfigCheckbox
						aria-labelledby={`${id} ${headCheckboxIds.EMAIL}`}
						id={emailCheckboxId}
						isDisabled={isEmailNotificationDisabled}
						name={emailCheckboxId}
						testId={`personal-settings-notifications-config-table.ui.notifications-config-row-lite.${emailCheckboxId}`}
						isChecked={!!preference.emailChannel.isEnabled}
						onChange={(ev) => onChange(ev, 'EMAIL', type)}
					/>
				)}
			</CellContainer>
		</TableRowContainer>
	);
};

const hidden = css({ display: 'none' });

const border = css({ boxShadow: `inset 0 -1px 0 0 ${token('color.border')}` });

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CellContainer = styled.td({
	borderCollapse: 'separate',
	appearance: 'none',
	boxSizing: 'border-box',
	display: 'table-cell',

	color: token('color.text.accent.gray'),
	paddingRight: token('space.050'),
	fontWeight: token('font.weight.regular'),
	paddingLeft: token('space.050'),
	verticalAlign: 'middle',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-of-type': {
		paddingLeft: token('space.500'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableRowContainer = styled.tr({
	height: `${token('space.600')}`,
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});
